// Third party
import React from 'react';
import moment from 'moment';

// Components
import { UnitInput, SelectInput, SelectOption, ToggleSwitch } from '../../../components/inputs';
import { InfoTooltip } from '../../../components/tooltips';

// Utils
import { formatMessage, FormattedMessage, DEFAULT_DECIMALS, formatNumber } from '../../../../utils/i18n';
import { REBALANCING_PERIODS } from '../../../../utils/portfolio/utils';
import { PERFORMANCE_CHART } from '../../../../utils/constants';
import ComparisonChartOptions from '@/types/comparison-chart-options';
import Nullable from '@/types/nullable';
import Locale from '@/types/locale';
import User from '@/types/user';
import RadioItem from '@/types/radio-item';
import GrowthChartOptions from '@/types/growth-chart-options';
import DrawdownChartOptions from '@/types/drawdown-chart-options';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import { filteredPerformanceAndRiskDataOptions } from '@/utils/preferences';
import { shouldUseMonthlyRollingInReturnsForRebalancing } from '@/utils/comparison';

type HandleProps = {
  handleChartOptionsChange: (options: ComparisonChartOptions, chart: PERFORMANCE_CHART) => void;
  handleBaseCurrencyChange: (baseCurrencyChange: boolean) => void;
  handleLongerHistoryChange: (toggled?: boolean, refreshProposalItems?: boolean) => void;
  handleMonthEndDataOptionChange: (monthEndDataOption?: PERIOD_END_DATA_OPTIONS) => void;
};

type Props = HandleProps & {
  capital?: number;
  useMonthly?: boolean;
  useMonthlyRebalancing?: boolean;
  showCurrencyConversion?: boolean;
  showBaseCurrencyOption?: boolean;
  baseCurrencyConversion?: boolean;
  showLongerHistoryOption?: boolean;
  showLongerHistory?: boolean;
  showMonthEndDataOption?: boolean;
  showMonthlyRebalancingOption?: boolean;
  monthEndDataOption?: PERIOD_END_DATA_OPTIONS;
  autoMonthEndData?: boolean;
  startDate?: Nullable<Date>;
  endDate?: Date;
  dark?: boolean;
  locale: Locale;
  rebalancePeriodValue?: string;
  user: User;
  isHistoricalScenariosTabActive: boolean;
};

type State = {
  capitalIsFocused: boolean;
  previousMonthlyRebalancingSettingValues?: {
    useMonthly?: boolean;
    rebalancePeriodValue?: string;
    monthEndDataOption?: PERIOD_END_DATA_OPTIONS;
  };
};
class ComparisonTopBarSettingsModal extends React.Component<Props, State> {
  readonly state: State = {
    capitalIsFocused: false,
    previousMonthlyRebalancingSettingValues: undefined,
  };

  readonly REBALANCING_OPTIONS = [
    { label: formatMessage('Charts.Growth.RebalanceOptions.Daily'), value: REBALANCING_PERIODS.DAILY.value },
    { label: formatMessage('Charts.Growth.RebalanceOptions.Quarterly'), value: REBALANCING_PERIODS.QUARTERLY.value },
    { label: formatMessage('Charts.Growth.RebalanceOptions.Semiannually'), value: REBALANCING_PERIODS.SEMIANNUALLY.value },
    { label: formatMessage('Charts.Growth.RebalanceOptions.Yearly'), value: REBALANCING_PERIODS.YEARLY.value },
  ];

  readonly MAX_CAPITAL = 1000000000;

  readonly DEFAULT_OPTIONS = {
    capital: 100000,
    rebalancePeriodValue: REBALANCING_PERIODS.DAILY.value,
  };

  readonly MONTH_END_DATA_RADIO_ITEMS: RadioItem[] = [
    {
      label: (
        <>
          <span>{formatMessage('Comparison.MonthEndData.RecentMonthEnd')}</span>
          <InfoTooltip
            className="ms-1"
            title={formatMessage('Comparison.MonthEndData.RecentMonthEnd')}
            message={formatMessage('Comparison.MonthEndData.RadioTooltip', {
              date: moment().subtract(1, 'months').endOf('month').format(formatMessage('Common.DateFormats.ShortMonthDay')),
            })}
          />
        </>
      ),
      value: PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END,
    },
    {
      label: (
        <>
          <span>{formatMessage('Comparison.MonthEndData.PreviousMonthEnd')}</span>
          <InfoTooltip
            className="ms-1"
            title={formatMessage('Comparison.MonthEndData.PreviousMonthEnd')}
            message={formatMessage('Comparison.MonthEndData.RadioTooltip', {
              date: moment().subtract(2, 'months').endOf('month').format(formatMessage('Common.DateFormats.ShortMonthDay')),
            })}
          />
        </>
      ),
      value: PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END,
    },
  ];

  readonly QUARTER_END_DATA_RADIO_ITEMS: RadioItem[] = [
    {
      label: (
        <>
          <span>{formatMessage('Comparison.QuarterEndData.RecentQuarterEnd')}</span>
          <InfoTooltip
            className="ms-1"
            title={formatMessage('Comparison.QuarterEndData.RecentQuarterEnd')}
            message={formatMessage('Comparison.QuarterEndData.RadioTooltip', {
              date: moment().subtract(3, 'months').endOf('quarter').format(formatMessage('Common.DateFormats.ShortMonthDay')),
            })}
          />
        </>
      ),
      value: PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END,
    },
    {
      label: (
        <>
          <span>{formatMessage('Comparison.QuarterEndData.PreviousQuarterEnd')}</span>
          <InfoTooltip
            className="ms-1"
            title={formatMessage('Comparison.QuarterEndData.PreviousQuarterEnd')}
            message={formatMessage('Comparison.QuarterEndData.RadioTooltip', {
              date: moment().subtract(6, 'months').endOf('quarter').format(formatMessage('Common.DateFormats.ShortMonthDay')),
            })}
          />
        </>
      ),
      value: PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END,
    },
  ];

  formatCapital = (capital: number) => {
    const { capitalIsFocused } = this.state;
    return capitalIsFocused ? capital : formatNumber(capital, DEFAULT_DECIMALS, this.props.locale);
  };

  onGrowthChartOptionsChange = <K extends keyof GrowthChartOptions>(options: Pick<GrowthChartOptions, K>) => {
    this.props.handleChartOptionsChange(options, PERFORMANCE_CHART.GROWTH);
  };

  onDrawdownChartOptionsChange = (options: DrawdownChartOptions) => {
    this.props.handleChartOptionsChange(options, PERFORMANCE_CHART.DRAWDOWN);
  };

  onCapitalChange = (value: string) => {
    const capital = value ? Math.min(Number(+value), this.MAX_CAPITAL) : 0;
    this.onGrowthChartOptionsChange({ capital });
  };

  onFocusCapital = () => {
    this.setState({ capitalIsFocused: true });
  };

  onBlurCapitalChange = () => {
    const { capital } = this.props;
    if (capital === undefined) {
      this.onGrowthChartOptionsChange({ capital });
    }
    this.setState({ capitalIsFocused: false });
  };

  onRebalanceChange = (value: any) => {
    const rebalancePeriod = REBALANCING_PERIODS[value.toUpperCase()];
    this.onGrowthChartOptionsChange({ rebalancePeriod });
  };

  onMonthlyRebalancingChange = (selected: boolean) => {
    if (selected) {
      const otherPropertiesChange = {
        useMonthly: selected,
        rebalancePeriod: REBALANCING_PERIODS.DAILY,
      };
      this.setState(() => ({
        previousMonthlyRebalancingSettingValues: {
          useMonthly: this.props.useMonthly,
          rebalancePeriodValue: this.props.rebalancePeriodValue,
          monthEndDataOption: this.props.monthEndDataOption,
        },
      }));
      this.onGrowthChartOptionsChange({ useMonthlyRebalancing: selected, ...otherPropertiesChange });
      this.onDrawdownChartOptionsChange({ useMonthlyRebalancing: selected, ...otherPropertiesChange });
      if (this.props.monthEndDataOption === PERIOD_END_DATA_OPTIONS.NONE) {
        this.onMonthEndDataChanged(PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END);
      }
    } else {
      let otherPropertiesChange = {};
      if (this.state.previousMonthlyRebalancingSettingValues) {
        otherPropertiesChange = {
          useMonthly: this.state.previousMonthlyRebalancingSettingValues?.useMonthly,
          rebalancePeriod: this.state.previousMonthlyRebalancingSettingValues.rebalancePeriodValue
            ? REBALANCING_PERIODS[this.state.previousMonthlyRebalancingSettingValues.rebalancePeriodValue.toUpperCase()]
            : undefined,
        };
      }
      this.onGrowthChartOptionsChange({ useMonthlyRebalancing: selected, ...otherPropertiesChange });
      this.onDrawdownChartOptionsChange({ useMonthlyRebalancing: selected, ...otherPropertiesChange });
      if (this.state.previousMonthlyRebalancingSettingValues) {
        this.onMonthEndDataChanged(this.state.previousMonthlyRebalancingSettingValues.monthEndDataOption);
      }
    }
  };

  onIntervalChange = (selected: boolean) => {
    this.onGrowthChartOptionsChange({ useMonthly: selected });
    this.onDrawdownChartOptionsChange({ useMonthly: selected });
  };

  onShowLongerHistoryChange = (toggled: boolean) => {
    this.props.handleLongerHistoryChange(toggled, false);
  };

  onToggleUseAuto = (useAuto: boolean) => {
    const { user } = this.props;
    if (useAuto) {
      this.props.handleMonthEndDataOptionChange(PERIOD_END_DATA_OPTIONS.AUTO);
    } else {
      this.props.handleMonthEndDataOptionChange(user.preferences?.autoPerformanceAndRiskData as PERIOD_END_DATA_OPTIONS);
    }
  };

  hasSelectedMonthlyRebalancingCombination = () => {
    const { monthEndDataOption, rebalancePeriodValue, useMonthly, useMonthlyRebalancing, user } = this.props;
    return !!(
      shouldUseMonthlyRollingInReturnsForRebalancing(monthEndDataOption, user) &&
      (rebalancePeriodValue === undefined || rebalancePeriodValue === REBALANCING_PERIODS.DAILY.value) &&
      useMonthly &&
      !useMonthlyRebalancing
    );
  };

  onMonthEndDataChanged = (value: any) => {
    this.props.handleMonthEndDataOptionChange(value);
    this.setState(() => ({
      previousMonthlyRebalancingSettingValues: {
        ...this.state.previousMonthlyRebalancingSettingValues,
        monthEndDataOption: value,
      },
    }));
  };

  hasMonthEndDataOption = (): boolean => {
    const value =
      !!this.props.monthEndDataOption &&
      (this.props.monthEndDataOption === PERIOD_END_DATA_OPTIONS.PREVIOUS_MONTH_END ||
        this.props.monthEndDataOption === PERIOD_END_DATA_OPTIONS.RECENT_MONTH_END);
    return value;
  };

  hasQuarterEndDataOption = (): boolean =>
    !!this.props.monthEndDataOption &&
    (this.props.monthEndDataOption === PERIOD_END_DATA_OPTIONS.PREVIOUS_QUARTER_END ||
      this.props.monthEndDataOption === PERIOD_END_DATA_OPTIONS.RECENT_QUARTER_END);

  render() {
    const {
      capital,
      dark,
      rebalancePeriodValue,
      showLongerHistoryOption,
      showLongerHistory,
      useMonthly,
      showMonthlyRebalancingOption,
      useMonthlyRebalancing,
      showBaseCurrencyOption,
      baseCurrencyConversion,
      handleBaseCurrencyChange,
      showMonthEndDataOption,
      monthEndDataOption,
      user,
      autoMonthEndData,
      isHistoricalScenariosTabActive,
    } = this.props;
    const monthlyReblancingActivatedByCombination = this.hasSelectedMonthlyRebalancingCombination();
    return (
      <div className="settings-option-container">
        <div className="settings-title">
          <div className="settings-modal-title">
            <FormattedMessage id="SiteNavigation.Settings" />
          </div>
        </div>
        <div className="settings-modal-section-divider">
          <div className="settings-modal-section-title">
            <FormattedMessage id="Comparison.ComparisonSettingsModal.TitleGraph" />
          </div>
        </div>
        <div className="settings-options-list">
          <div className="settings-option-row">
            <label className="settings-option-label" htmlFor="initial-capital-input">
              <FormattedMessage id="Charts.Growth.InitialCapital" />
            </label>
            <div className="settings-option-control">
              <UnitInput
                id="initial-capital-input"
                type="number"
                value={capital !== undefined ? capital : this.DEFAULT_OPTIONS.capital}
                onValueChange={this.onCapitalChange}
                onBlur={this.onBlurCapitalChange}
                onFocus={this.onFocusCapital}
                unitSymbol="$"
                label={formatMessage('Charts.Growth.InitialCapital')}
                hasHiddenLabel
              />
            </div>
          </div>
          <div className="settings-option-row">
            <label className="settings-option-label" htmlFor="rebalance-select">
              <FormattedMessage id="Charts.Growth.Rebalance" />
            </label>
            <div className="settings-option-control">
              <SelectInput
                id="rebalance-select"
                compact
                dark={dark}
                onValueChange={this.onRebalanceChange}
                selected={rebalancePeriodValue != null ? rebalancePeriodValue : this.DEFAULT_OPTIONS.rebalancePeriodValue}
                className="rebalancing-select"
                disabled={useMonthlyRebalancing}
                label={formatMessage('Charts.Growth.Rebalance')}
                hasHiddenLabel
              >
                {this.REBALANCING_OPTIONS.map(opt => (
                  <SelectOption key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectOption>
                ))}
              </SelectInput>
            </div>
          </div>

          <div className="settings-option-row settings-option-toggle">
            <div className="settings-option-label-with-info">
              <span>
                <FormattedMessage id="Charts.UseMonthlyData.Title" />
              </span>
              <InfoTooltip message={formatMessage('Charts.UseMonthlyData.Disclaimer')} />
            </div>

            <div className="settings-option-control">
              <ToggleSwitch
                dark={dark}
                checked={useMonthly}
                disabled={useMonthlyRebalancing || isHistoricalScenariosTabActive}
                onChange={this.onIntervalChange}
                aria-label={formatMessage('Charts.UseMonthlyData.Title')}
                data-testid="comparisonTopBarSettingsModal-toggleSwitch-monthlyData"
              />
            </div>
          </div>
        </div>
        <div className="settings-modal-section-divider">
          <div className="settings-modal-section-title">
            <FormattedMessage id="Comparison.ComparisonSettingsModal.TitlePerformanceAndMetric" />
          </div>
        </div>

        <div className="settings-options-list">
          {showMonthlyRebalancingOption && (
            <div className="settings-option-row settings-option-toggle">
              <div className="settings-option-label-with-info">
                <span>
                  <FormattedMessage id="Charts.UseMonthlyRebalancing.Title" />
                </span>
                <InfoTooltip message={formatMessage('Charts.UseMonthlyRebalancing.Disclaimer')} />
              </div>

              <div className="settings-option-control">
                <ToggleSwitch
                  dark={dark}
                  checked={useMonthlyRebalancing || monthlyReblancingActivatedByCombination}
                  disabled={monthlyReblancingActivatedByCombination}
                  onChange={this.onMonthlyRebalancingChange}
                  aria-label={formatMessage('Charts.UseMonthlyRebalancing.Title')}
                />
              </div>
            </div>
          )}

          {showBaseCurrencyOption && (
            <div className="settings-option-row settings-option-toggle">
              <div className="settings-option-label-with-info">
                <span>
                  <FormattedMessage id="Comparison.ComparisonSettingsModal.TitleBaseCurrency" />
                </span>
                <InfoTooltip message={formatMessage('Comparison.BaseCurrencyDisabled')} />
              </div>

              <div className="settings-option-control">
                <ToggleSwitch
                  dark={dark}
                  checked={baseCurrencyConversion}
                  onChange={handleBaseCurrencyChange}
                  aria-label={formatMessage('Comparison.ComparisonSettingsModal.TitleBaseCurrency')}
                />
              </div>
            </div>
          )}

          {showLongerHistoryOption && (
            <div className="settings-option-row settings-option-toggle">
              <div className="settings-option-label-with-info">
                <span>
                  <FormattedMessage id="Comparison.ShowingLongerHistory" />
                </span>
                <InfoTooltip message={formatMessage('Disclaimers.ShortHistoryDisabled')} />
              </div>
              <div className="settings-option-control">
                <ToggleSwitch
                  dark={dark}
                  onChange={this.onShowLongerHistoryChange}
                  checked={showLongerHistory != null ? showLongerHistory : false}
                  aria-label={formatMessage('Comparison.ShowingLongerHistory')}
                />
              </div>
            </div>
          )}

          {showMonthEndDataOption && (
            <>
              <div className="settings-option-row">
                <label className="settings-option-label" htmlFor="period-end-select">
                  <FormattedMessage id="Comparison.ComparisonSettingsModal.PeriodEnd" />
                </label>
                <div className="settings-option-control">
                  <SelectInput
                    id="period-end-select"
                    disabled={autoMonthEndData || isHistoricalScenariosTabActive}
                    compact
                    dark={dark}
                    onValueChange={this.onMonthEndDataChanged}
                    selected={monthEndDataOption}
                    className="rebalancing-select"
                    label={formatMessage('Comparison.ComparisonSettingsModal.PeriodEnd')}
                    hasHiddenLabel
                    data-testid="comparisonTopBarSettingsModal-selectInput-periodEnd"
                  >
                    {filteredPerformanceAndRiskDataOptions(user, false, useMonthlyRebalancing).map(option => (
                      <SelectOption key={option.value} value={option.value}>
                        {formatMessage(option.i18nKey)}
                      </SelectOption>
                    ))}
                  </SelectInput>
                </div>
              </div>
              {user.preferences?.autoPerformanceAndRiskData && (
                <div className="settings-option-row settings-option-toggle">
                  <label className="settings-option-label">
                    <FormattedMessage id="Settings.Preferences.ComparisonSettings.Fields.PerformanceAndRiskData.Options.Auto" />
                  </label>
                  <div className="settings-option-control">
                    <ToggleSwitch
                      dark={dark}
                      checked={autoMonthEndData}
                      onChange={this.onToggleUseAuto}
                      aria-label={formatMessage('Settings.Preferences.ComparisonSettings.Fields.PerformanceAndRiskData.Options.Auto')}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

export default ComparisonTopBarSettingsModal;
